import React from 'react'
import { makeStyles } from '@mui/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import orgamax from 'ampersand-app'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { experimentalFeatures } from '@/modules/base/util/constants'
import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { showResetInvoiceReviewError } from '@/modules/document/services/reset-invoice-review'
import {
  awaitableConfirmationModal,
  AwaitableConfirmationModalIcons,
} from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '0 0 12px',
    padding: '10px 12px',
    borderRadius: 4,
    backgroundColor: theme.palette?.info?.light ?? '#e8f2fb',
    fontSize: 13,
  },
  icon: {
    fontSize: 18,
    flexShrink: 0,
  },
  text: {
    flexGrow: 1,
  },
  action: {
    flexShrink: 0,
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
    color: 'inherit',
  },
}))

/**
 * FLAG:useDocuAi — „Eine neuere KI-Analyse liegt vor."
 *
 * Scenario: „Erneut analysieren" starts a genuine new run, but the result is deliberately NOT
 * imposed on the existing review (server-side F3 guard). Without a notice, the run therefore
 * ends invisibly — the review component continues showing the old values, and nobody learns
 * whether or when anything new arrived. That was the exact feedback.
 *
 * The marker comes from the server (`metaData.freshAnalysisAt`,
 * set in analyzeFile when a terminal
 * result passes an already completed review) — not from a client-side timestamp comparison,
 * because ordinary saves also touch the review row and would make it an unreliable signal.
 *
 * „Werte übernehmen" is deliberately NOT a new endpoint: it calls „In Prüfung zurücksetzen",
 * which does exactly this (re-derive from the stored — now fresh — analysis), including its
 * confirmation and refusal for a booked expense. A second adoption implementation beside it
 * would be where the two diverge.
 */
const FreshAnalysisBanner = (props: { file: any; onAdopted?: () => void }) => {
  const classes = useStyles(props)
  const { file, onAdopted } = props
  const [busy, setBusy] = React.useState(false)

  const useDocuAiEnabled = orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const freshAnalysisAt = file?.metaData?.freshAnalysisAt
  if (!useDocuAiEnabled || !freshAnalysisAt || !file?.id) { return null }

  const handleAdopt = async() => {
    const confirmed = await awaitableConfirmationModal({
      isOpen: true,
      title: lang.documents.freshAnalysis.confirmTitle,
      description: lang.documents.freshAnalysis.confirmDescription,
      confirmLabel: lang.documents.freshAnalysis.confirm,
      cancelLabel: lang.documents.freshAnalysis.cancel,
      confirmIcon: AwaitableConfirmationModalIcons.checkIcon,
    })
    if (!confirmed) { return }

    setBusy(true)
    try {
      await FileService.resetInvoiceReview(file.id)
      toast.showToast({ message: lang.documents.freshAnalysis.adopted })
      onAdopted?.()
    } catch (error) {
      showResetInvoiceReviewError(error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={classes.banner} data-testid='fresh-analysis-banner'>
      <InfoOutlinedIcon className={classes.icon} />
      <span className={classes.text}>
        <strong>{lang.documents.freshAnalysis.headline}</strong>{' '}
        {lang.documents.freshAnalysis.body}
      </span>
      <button
        type='button'
        className={classes.action}
        onClick={handleAdopt}
        disabled={busy}
        data-qs-id='freshAnalysis-adopt'
      >
        {lang.documents.freshAnalysis.action}
      </button>
    </div>
  )
}

export default FreshAnalysisBanner
